import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { ArgumentFingerprint } from "@/lib/argumentFingerprint";

// --- Types ---

export interface Claim {
  id: string;
  concept_id: string;
  text: string;
  claim_type: "thesis" | "supporting" | "counter" | "synthesis" | "gap";
  status: "draft" | "supported" | "contested";
  evidence_count: number;
  pos_x: number | null;
  pos_y: number | null;
  created_at: string;
  updated_at: string;
}

export interface ClaimLink {
  id: string;
  source_claim_id: string;
  target_claim_id: string;
  relation: "supports" | "challenges" | "depends_on" | "qualifies";
  created_at: string;
}

export interface ClaimEvidence {
  id: string;
  claim_id: string;
  paper_id: string;
  concept_paper_id: string | null;
  excerpt_text: string;
  page_number: number | null;
  relation: "supports" | "challenges" | "qualifies";
  note: string | null;
  created_at: string;
}

export interface MatrixClaim {
  id: string;
  text: string;
  claim_type: "thesis" | "supporting" | "counter" | "synthesis" | "gap";
}

export interface MatrixPaper {
  paper_id: string;
  short_label: string;
  year: number | null;
  title: string;
}

export interface MatrixEvidence {
  claim_id: string;
  paper_id: string;
  relation: "supports" | "challenges" | "qualifies";
}

export interface MatrixAuditIssue {
  severity: number;
  text: string;
  claim_id: string | null;
}

export interface MatrixResponse {
  claims: MatrixClaim[];
  papers: MatrixPaper[];
  evidence: MatrixEvidence[];
  completeness_score: number;
  audit_issues: MatrixAuditIssue[];
}

export interface ArgumentAnalysis {
  weak_claims: { claim_text: string; reason: string }[];
  contradictions: { claim_a: string; claim_b: string; explanation: string }[];
  logical_gaps: { description: string; between_claims: string[] }[];
  overall_assessment: string;
}

export interface StoredArgumentAnalysis {
  result: ArgumentAnalysis;
  generated_at: string;
  model: string;
  fingerprint?: ArgumentFingerprint;
}

// --- Queries ---

export function useEvidenceStats(conceptId: string) {
  return useQuery({
    queryKey: ["evidence-stats", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/claims/stats`)
        .json<{ unique_papers_with_evidence: number }>(),
    enabled: !!conceptId,
  });
}

export function useCompletenessMatrix(conceptId: string) {
  return useQuery({
    queryKey: ["completeness-matrix", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/claims/matrix`)
        .json<MatrixResponse>(),
    enabled: !!conceptId,
  });
}

export function useClaims(conceptId: string) {
  return useQuery({
    queryKey: ["claims", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/claims`)
        .json<Claim[]>(),
    enabled: !!conceptId,
  });
}

export function useClaimLinks(conceptId: string) {
  return useQuery({
    queryKey: ["claim-links", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/claims/links`)
        .json<ClaimLink[]>(),
    enabled: !!conceptId,
  });
}

export function useClaimEvidence(conceptId: string, claimId: string | null) {
  return useQuery({
    queryKey: ["claim-evidence", claimId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/claims/${claimId}/evidence`)
        .json<ClaimEvidence[]>(),
    enabled: !!conceptId && !!claimId,
  });
}

// --- Mutations ---

export function useClaimActions(conceptId: string) {
  const queryClient = useQueryClient();
  const claimsKey = ["claims", conceptId];
  const linksKey = ["claim-links", conceptId];
  const evidenceStatsKey = ["evidence-stats", conceptId];

  const createClaim = useMutation({
    mutationFn: (data: {
      text: string;
      claim_type?: string;
    }) =>
      apiClient
        .post(`api/concepts/${conceptId}/claims`, { json: data })
        .json<Claim>(),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: claimsKey });
      const previous = queryClient.getQueryData<Claim[]>(claimsKey);
      const optimistic: Claim = {
        id: `temp-${crypto.randomUUID()}`,
        concept_id: conceptId,
        text: data.text,
        claim_type: (data.claim_type ?? "supporting") as Claim["claim_type"],
        status: "draft",
        evidence_count: 0,
        pos_x: null,
        pos_y: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      queryClient.setQueryData<Claim[]>(claimsKey, (old) =>
        old ? [...old, optimistic] : [optimistic],
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(claimsKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: claimsKey });
      queryClient.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] });
    },
  });

  const updateClaim = useMutation({
    mutationFn: ({
      claimId,
      ...data
    }: {
      claimId: string;
      text?: string;
      claim_type?: string;
    }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/claims/${claimId}`, { json: data })
        .json<Claim>(),
    onMutate: async ({ claimId, ...data }) => {
      await queryClient.cancelQueries({ queryKey: claimsKey });
      const previous = queryClient.getQueryData<Claim[]>(claimsKey);
      queryClient.setQueryData<Claim[]>(claimsKey, (old) =>
        old?.map((c) =>
          c.id === claimId
            ? {
                ...c,
                ...(data.text !== undefined && { text: data.text }),
                ...(data.claim_type !== undefined && { claim_type: data.claim_type as Claim["claim_type"] }),
                updated_at: new Date().toISOString(),
              }
            : c,
        ),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(claimsKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: claimsKey });
      queryClient.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] });
    },
  });

  const deleteClaim = useMutation({
    mutationFn: (claimId: string) =>
      apiClient.delete(`api/concepts/${conceptId}/claims/${claimId}`),
    onMutate: async (claimId) => {
      await queryClient.cancelQueries({ queryKey: claimsKey });
      const previous = queryClient.getQueryData<Claim[]>(claimsKey);
      queryClient.setQueryData<Claim[]>(claimsKey, (old) =>
        old?.filter((c) => c.id !== claimId),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(claimsKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: claimsKey });
      queryClient.invalidateQueries({ queryKey: linksKey });
      queryClient.invalidateQueries({ queryKey: evidenceStatsKey });
      queryClient.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] });
      // Papers carry a per-paper has_evidence flag — deleting a claim
      // cascades its evidence, so the list must refetch.
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });

  const restoreClaim = useMutation({
    mutationFn: (claimId: string) =>
      apiClient
        .post(`api/concepts/${conceptId}/claims/${claimId}/restore`)
        .json<Claim>(),
    onSettled: () => {
      // Restore brings the claim + its links + evidence back — refetch the
      // same surfaces delete invalidated.
      queryClient.invalidateQueries({ queryKey: claimsKey });
      queryClient.invalidateQueries({ queryKey: linksKey });
      queryClient.invalidateQueries({ queryKey: evidenceStatsKey });
      queryClient.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });

  const setClaimPosition = useMutation({
    mutationFn: ({ claimId, pos_x, pos_y }: { claimId: string; pos_x: number; pos_y: number }) =>
      apiClient.patch(`api/concepts/${conceptId}/claims/${claimId}/position`, {
        json: { pos_x, pos_y },
      }),
    onMutate: async ({ claimId, pos_x, pos_y }) => {
      // Optimistically stamp the position into the claims cache so a later
      // re-derive of the canvas keeps the node where the user dropped it.
      await queryClient.cancelQueries({ queryKey: claimsKey });
      const previous = queryClient.getQueryData<Claim[]>(claimsKey);
      queryClient.setQueryData<Claim[]>(claimsKey, (old) =>
        old?.map((c) => (c.id === claimId ? { ...c, pos_x, pos_y } : c)),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(claimsKey, context.previous);
      }
    },
  });

  const createLink = useMutation({
    mutationFn: (data: {
      source_claim_id: string;
      target_claim_id: string;
      relation?: string;
    }) =>
      apiClient
        .post(`api/concepts/${conceptId}/claims/links`, { json: data })
        .json<ClaimLink>(),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: linksKey });
      const previous = queryClient.getQueryData<ClaimLink[]>(linksKey);
      const optimistic: ClaimLink = {
        id: `temp-${Date.now()}`,
        source_claim_id: data.source_claim_id,
        target_claim_id: data.target_claim_id,
        relation: (data.relation ?? "supports") as ClaimLink["relation"],
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<ClaimLink[]>(linksKey, (old) =>
        old ? [...old, optimistic] : [optimistic],
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(linksKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: linksKey });
    },
  });

  const deleteLink = useMutation({
    mutationFn: (linkId: string) =>
      apiClient.delete(`api/concepts/${conceptId}/claims/links/${linkId}`),
    onMutate: async (linkId) => {
      await queryClient.cancelQueries({ queryKey: linksKey });
      const previous = queryClient.getQueryData<ClaimLink[]>(linksKey);
      queryClient.setQueryData<ClaimLink[]>(linksKey, (old) =>
        old?.filter((l) => l.id !== linkId),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(linksKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: linksKey });
    },
  });

  const updateLink = useMutation({
    mutationFn: ({
      linkId,
      ...data
    }: {
      linkId: string;
      relation: string;
    }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/claims/links/${linkId}`, {
          json: data,
        })
        .json<ClaimLink>(),
    onMutate: async ({ linkId, ...data }) => {
      await queryClient.cancelQueries({ queryKey: linksKey });
      const previous = queryClient.getQueryData<ClaimLink[]>(linksKey);
      queryClient.setQueryData<ClaimLink[]>(linksKey, (old) =>
        old?.map((l) =>
          l.id === linkId
            ? { ...l, relation: data.relation as ClaimLink["relation"] }
            : l,
        ),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(linksKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: linksKey });
    },
  });

  const analyzeArgument = useMutation({
    // mutationKey so any component can observe pending state via
    // useIsMutating — isPending is instance-local.
    mutationKey: ["argument-analyze", conceptId],
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/claims/analyze`, {
          timeout: 120_000,
        })
        .json<StoredArgumentAnalysis>(),
    onSuccess: () => {
      // Result is persisted on the concept — refetch to pick it up.
      // Key is PLURAL ["concepts", id] (useConcept's key); the singular
      // ["concept", id] family belongs to useMonitor.
      queryClient.invalidateQueries({ queryKey: ["concepts", conceptId] });
    },
  });

  return { createClaim, updateClaim, deleteClaim, restoreClaim, setClaimPosition, createLink, deleteLink, updateLink, analyzeArgument };
}

export function useEvidenceActions(conceptId: string, claimId: string) {
  const queryClient = useQueryClient();

  const evidenceKey = ["claim-evidence", claimId];
  const claimsKey = ["claims", conceptId];
  const evidenceStatsKey = ["evidence-stats", conceptId];

  const addEvidence = useMutation({
    mutationFn: (data: {
      paper_id: string;
      concept_paper_id?: string;
      excerpt_text: string;
      page_number?: number;
      relation?: string;
      note?: string;
    }) =>
      apiClient
        .post(`api/concepts/${conceptId}/claims/${claimId}/evidence`, {
          json: data,
        })
        .json<ClaimEvidence>(),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: evidenceKey });
      const previous = queryClient.getQueryData<ClaimEvidence[]>(evidenceKey);
      const optimistic: ClaimEvidence = {
        id: `temp-${Date.now()}`,
        claim_id: claimId,
        paper_id: data.paper_id,
        concept_paper_id: data.concept_paper_id ?? null,
        excerpt_text: data.excerpt_text,
        page_number: data.page_number ?? null,
        relation: (data.relation ?? "supports") as ClaimEvidence["relation"],
        note: data.note ?? null,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<ClaimEvidence[]>(evidenceKey, (old) =>
        old ? [...old, optimistic] : [optimistic],
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(evidenceKey, context.previous);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: evidenceKey });
      queryClient.invalidateQueries({ queryKey: claimsKey });
      queryClient.invalidateQueries({ queryKey: evidenceStatsKey });
      queryClient.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] });
      // Papers carry a per-paper has_evidence flag — keep the list in sync.
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      // Defensive — the stash flow already populated the highlights cache,
      // but this invalidation reconciles if a second client attached evidence
      // for the same paper while this tab was open.
      queryClient.invalidateQueries({
        queryKey: ["highlights", variables.paper_id],
      });
    },
  });

  const updateEvidence = useMutation({
    mutationFn: ({
      evidenceId,
      ...data
    }: {
      evidenceId: string;
      relation?: string;
      note?: string;
    }) =>
      apiClient
        .patch(
          `api/concepts/${conceptId}/claims/${claimId}/evidence/${evidenceId}`,
          { json: data },
        )
        .json<ClaimEvidence>(),
    onMutate: async ({ evidenceId, ...data }) => {
      await queryClient.cancelQueries({ queryKey: evidenceKey });
      const previous = queryClient.getQueryData<ClaimEvidence[]>(evidenceKey);
      queryClient.setQueryData<ClaimEvidence[]>(evidenceKey, (old) =>
        old?.map((ev) =>
          ev.id === evidenceId
            ? {
                ...ev,
                ...(data.relation !== undefined && { relation: data.relation as ClaimEvidence["relation"] }),
                ...(data.note !== undefined && { note: data.note }),
              }
            : ev,
        ),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(evidenceKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: evidenceKey });
      // Changing an evidence relation changes the claim's DERIVED status
      // (supported ↔ contested ↔ draft), so the claims list must refetch or the
      // node dot + slide-over badge go stale (matches add/deleteEvidence).
      queryClient.invalidateQueries({ queryKey: claimsKey });
      queryClient.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] });
    },
  });

  const deleteEvidence = useMutation({
    mutationFn: (evidenceId: string) =>
      apiClient.delete(
        `api/concepts/${conceptId}/claims/${claimId}/evidence/${evidenceId}`,
      ),
    onMutate: async (evidenceId) => {
      await queryClient.cancelQueries({ queryKey: evidenceKey });
      const previous = queryClient.getQueryData<ClaimEvidence[]>(evidenceKey);
      queryClient.setQueryData<ClaimEvidence[]>(evidenceKey, (old) =>
        old?.filter((ev) => ev.id !== evidenceId),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(evidenceKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: evidenceKey });
      queryClient.invalidateQueries({ queryKey: claimsKey });
      queryClient.invalidateQueries({ queryKey: evidenceStatsKey });
      queryClient.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] });
      // Papers carry a per-paper has_evidence flag — keep the list in sync.
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });

  return { addEvidence, updateEvidence, deleteEvidence };
}
